import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Package,
  RefreshCw,
  Search,
  Store,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api, categoriesAPI, ordersAPI, shopsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const toastStyles = {
  success: {
    style: {
      background: '#16a34a',
      color: '#ffffff'
    }
  },
  danger: {
    style: {
      background: '#dc2626',
      color: '#ffffff'
    }
  }
};

const OutOfStockSales = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canReplenishAll = user?.role !== 'manager';
  const [filters, setFilters] = useState({
    search: '',
    shop_id: '',
    category_id: '',
    bottle_size: '',
    start_date: '',
    end_date: '',
    page: 1,
    limit: 20
  });
  const [showReplenishModal, setShowReplenishModal] = useState(false);
  const [replenishingRecord, setReplenishingRecord] = useState(null);
  const [replenishData, setReplenishData] = useState(null);
  const [replenishQuantity, setReplenishQuantity] = useState(0);
  const [isReplenishingAll, setIsReplenishingAll] = useState(false);

  const { data: shopsData } = useQuery({
    queryKey: ['shops'],
    queryFn: () => shopsAPI.getAll().then(res => res.data)
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesAPI.getAll().then(res => res.data)
  });

  const {
    data: outOfStockData,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['out-of-stock-sales', filters],
    queryFn: () => ordersAPI.getOutOfStockSales(filters).then(res => res.data)
  });

  const shops = Array.isArray(shopsData?.shops) ? shopsData.shops : [];
  const categories = Array.isArray(categoriesData?.flat)
    ? categoriesData.flat
    : (Array.isArray(categoriesData?.categories) ? categoriesData.categories : []);
  const records = Array.isArray(outOfStockData?.records) ? outOfStockData.records : [];
  const pagination = outOfStockData?.pagination || { total: 0, page: 1, totalPages: 1, limit: filters.limit };

  const refreshOutOfStockSales = () => {
    queryClient.invalidateQueries({ queryKey: ['out-of-stock-sales'] });
    queryClient.invalidateQueries({ queryKey: ['stock-levels'] });
    queryClient.invalidateQueries({ queryKey: ['inventory-stats'] });
    queryClient.refetchQueries({ queryKey: ['out-of-stock-sales'], type: 'active' });
  };

  const removeReplenishedRecords = ({ product_id, location_id }) => {
    queryClient.setQueriesData({ queryKey: ['out-of-stock-sales'] }, (oldData) => {
      if (!oldData?.records) return oldData;

      const records = oldData.records.filter(record =>
        record.product_id !== product_id || record.shop_id !== location_id
      );
      const removedCount = oldData.records.length - records.length;

      return {
        ...oldData,
        records,
        pagination: oldData.pagination
          ? {
            ...oldData.pagination,
            total: Math.max((oldData.pagination.total || 0) - removedCount, 0)
          }
          : oldData.pagination
      };
    });
  };

  const replenishMutation = useMutation({
    mutationFn: (data) => api.post('/inventory/replenish', data),
    onSuccess: (_response, variables) => {
      removeReplenishedRecords(variables);
      setShowReplenishModal(false);
      setReplenishingRecord(null);
      setReplenishData(null);
      setReplenishQuantity(0);
      refreshOutOfStockSales();
      setTimeout(refreshOutOfStockSales, 500);
      toast.success('Successfully', toastStyles.success);
    },
    onError: () => {
      toast.error('fail to restore in stock', toastStyles.danger);
    }
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({
      ...prev,
      page
    }));
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      shop_id: '',
      category_id: '',
      bottle_size: '',
      start_date: '',
      end_date: '',
      page: 1,
      limit: 20
    });
  };

  const handleReplenishRecord = async (record) => {
    try {
      setReplenishingRecord(record);
      const response = await api.get(`/inventory/stock-info/${record.product_id}/${record.shop_id}`);
      const stockInfo = response.data;
      const defaultQuantity = record.product_type === 'perfume'
        ? 1
        : Math.min(stockInfo.shop_sold || record.sold_quantity || 0, stockInfo.available_quantity || 0);

      setReplenishData({
        shop_sold: stockInfo.shop_sold || record.shop_sold || record.sold_quantity || 0,
        available_quantity: stockInfo.available_quantity || 0,
        shop_assigned: stockInfo.shop_assigned || 0,
        shop_remaining: stockInfo.shop_remaining || 0
      });
      setReplenishQuantity(defaultQuantity);
      setShowReplenishModal(true);
    } catch (error) {
      console.error('Error fetching replenish data:', error);
      toast.error('fail to restore in stock', toastStyles.danger);
    }
  };

  const handleReplenishSubmit = (event) => {
    event.preventDefault();

    if (!replenishingRecord || !replenishData) return;
    if (replenishQuantity <= 0) {
      toast.error('fail to restore in stock', toastStyles.danger);
      return;
    }

    if (replenishingRecord.product_type !== 'perfume' && replenishQuantity > replenishData.available_quantity) {
      toast.error('fail to restore in stock', toastStyles.danger);
      return;
    }

    replenishMutation.mutate({
      location_type: 'shop',
      location_id: replenishingRecord.shop_id,
      product_id: replenishingRecord.product_id,
      quantity: replenishQuantity
    });
  };

  const uniqueOutOfStockItems = (items) => {
    const unique = [];
    const seen = new Set();
    for (const record of items) {
      const key = `${record.product_id}:${record.shop_id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(record);
    }
    return unique;
  };

  const getReplenishQuantity = (record) => {
    if (record.product_type === 'perfume') return 1;
    return Math.min(
      Number(record.available_quantity || 0),
      Number(record.shop_sold || record.sold_quantity || 0)
    );
  };

  const handleReplenishAll = async () => {
    if (!canReplenishAll) {
      toast.error('Managers cannot replenish all products', toastStyles.danger);
      return;
    }
    if (isReplenishingAll || replenishMutation.isPending || records.length === 0) return;

    const confirmed = window.confirm(
      'Replenish all unique out-of-stock products on this list? Duplicate rows for the same product and shop will be replenished once.'
    );
    if (!confirmed) return;

    setIsReplenishingAll(true);
    try {
      const response = await ordersAPI.getOutOfStockSales({
        ...filters,
        include_all: true,
        page: 1,
        limit: 100
      });
      const allRecords = Array.isArray(response.data?.records) ? response.data.records : [];
      const uniqueRecords = uniqueOutOfStockItems(allRecords);

      if (uniqueRecords.length === 0) {
        toast.error('No out-of-stock products to replenish', toastStyles.danger);
        return;
      }

      let succeeded = 0;
      let failed = 0;

      for (const record of uniqueRecords) {
        const quantity = getReplenishQuantity(record);
        if (!quantity || quantity < 1) {
          failed += 1;
          continue;
        }

        try {
          await api.post('/inventory/replenish', {
            location_type: 'shop',
            location_id: record.shop_id,
            product_id: record.product_id,
            quantity
          });
          succeeded += 1;
        } catch (error) {
          console.error('Replenish all item failed:', record.product_name, error);
          failed += 1;
        }
      }

      refreshOutOfStockSales();
      if (succeeded > 0 && failed === 0) {
        toast.success(`Replenished ${succeeded} product(s)`, toastStyles.success);
      } else if (succeeded > 0) {
        toast.success(`Replenished ${succeeded} product(s). ${failed} failed.`, toastStyles.success);
      } else {
        toast.error('fail to restore in stock', toastStyles.danger);
      }
    } catch (error) {
      console.error('Replenish all error:', error);
      toast.error('fail to restore in stock', toastStyles.danger);
    } finally {
      setIsReplenishingAll(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'RWF'
    }).format(amount || 0);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <AlertTriangle className="h-8 w-8 mr-3 text-red-600" />
            Out of Stock Sales
          </h1>
          <p className="text-gray-600 mt-2">
            Sale records for products that are currently out of stock and ready for Replenish from Sales.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          {canReplenishAll && (
            <button
              onClick={handleReplenishAll}
              className="inline-flex items-center justify-center px-4 py-2 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 disabled:opacity-50"
              disabled={isFetching || isReplenishingAll || replenishMutation.isPending || records.length === 0}
            >
              <RefreshCw className={`h-5 w-5 mr-2 ${isReplenishingAll ? 'animate-spin' : ''}`} />
              {isReplenishingAll ? 'Replenishing All...' : 'Replenish All'}
            </button>
          )}
          <button
            onClick={refreshOutOfStockSales}
            className="inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 disabled:opacity-50"
            disabled={isFetching || isReplenishingAll}
          >
            <RefreshCw className={`h-5 w-5 mr-2 ${isFetching && !isReplenishingAll ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(event) => handleFilterChange('search', event.target.value)}
                placeholder="Search product, SKU, or order number..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Shop</label>
            <select
              value={filters.shop_id}
              onChange={(event) => handleFilterChange('shop_id', event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">All Shops</option>
              {shops.map(shop => (
                <option key={shop.id} value={shop.id}>{shop.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={filters.category_id}
              onChange={(event) => handleFilterChange('category_id', event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bottle Size</label>
            <select
              value={filters.bottle_size}
              onChange={(event) => handleFilterChange('bottle_size', event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">All Sizes</option>
              <option value="30">30 ML</option>
              <option value="50">50 ML</option>
              <option value="100">100 ML</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(event) => handleFilterChange('start_date', event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(event) => handleFilterChange('end_date', event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={resetFilters}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Out-of-stock sale records</h2>
            <p className="text-sm text-gray-500">{pagination.total || 0} records found</p>
          </div>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            Out of Stock
          </span>
        </div>

        {isLoading ? (
          <div className="p-10 text-center text-gray-500">Loading out-of-stock sales...</div>
        ) : records.length === 0 ? (
          <div className="p-10 text-center">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">No out-of-stock sales records found</p>
            <p className="text-sm text-gray-500 mt-1">Records will appear here when sold products reach zero stock.</p>
          </div>
        ) : (
          <>
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sold</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {records.map(record => (
                    <tr key={record.order_item_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{record.product_name}</div>
                        <div className="text-sm text-gray-500">{record.sku || 'No SKU'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                          {formatDate(record.sale_date)}
                        </div>
                        {record.order_number && (
                          <div className="text-xs text-gray-500 mt-1">{record.order_number}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center">
                          <Store className="h-4 w-4 mr-2 text-gray-400" />
                          {record.shop_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">{record.sold_quantity} Unit</div>
                        <div className="text-sm text-gray-500">{formatCurrency(record.total_price)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-green-700">{record.available_quantity || 0} Unit</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Out of Stock
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleReplenishRecord(record)}
                          className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                          disabled={isReplenishingAll || replenishMutation.isPending}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Replenish from Sales
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="lg:hidden p-4 space-y-4">
              {records.map(record => (
                <div key={record.order_item_id} className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{record.product_name}</h3>
                      <p className="text-sm text-gray-500">{record.order_number} - {formatDate(record.sale_date)}</p>
                    </div>
                    <span className="shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Out of Stock
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-500">Order Date</p>
                      <p className="font-medium text-gray-900">{formatDate(record.sale_date)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Shop</p>
                      <p className="font-medium text-gray-900">{record.shop_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Sold</p>
                      <p className="font-medium text-gray-900">{record.sold_quantity} Unit</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Available</p>
                      <p className="font-medium text-green-700">{record.available_quantity || 0} Unit</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Amount</p>
                      <p className="font-medium text-gray-900">{formatCurrency(record.total_price)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleReplenishRecord(record)}
                    className="w-full inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                    disabled={isReplenishingAll || replenishMutation.isPending}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Replenish from Sales
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                disabled={pagination.page <= 1}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </button>
              <button
                onClick={() => handlePageChange(Math.min(pagination.totalPages, pagination.page + 1))}
                disabled={pagination.page >= pagination.totalPages}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 disabled:opacity-50"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {showReplenishModal && replenishingRecord && replenishData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg mr-3">
                  <RefreshCw className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Replenish from Sales</h2>
                  <p className="text-sm text-gray-600">Restore stock for this out-of-stock sale record</p>
                </div>
              </div>
              <button
                onClick={() => setShowReplenishModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleReplenishSubmit} className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <h4 className="font-medium text-gray-900 mb-3">Current Status</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600">Product:</p>
                    <p className="font-semibold text-gray-900">{replenishingRecord.product_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Shop:</p>
                    <p className="font-semibold text-gray-900">{replenishingRecord.shop_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Order Date:</p>
                    <p className="font-semibold text-gray-900">{formatDate(replenishingRecord.sale_date)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Currently Assigned:</p>
                    <p className="font-semibold text-gray-900">{replenishData.shop_assigned} Unit</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Sold:</p>
                    <p className="font-semibold text-purple-600">{replenishData.shop_sold} Unit</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Remaining in Shop:</p>
                    <p className="font-semibold text-red-600">0 Unit</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Available Globally:</p>
                    <p className="font-semibold text-green-600">{replenishData.available_quantity} Unit</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity to Replenish *
                </label>
                <input
                  type="number"
                  value={replenishQuantity}
                  onChange={(event) => setReplenishQuantity(parseInt(event.target.value, 10) || 0)}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    replenishingRecord.product_type === 'perfume' ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  min="1"
                  max={replenishingRecord.product_type === 'perfume' ? 1 : Math.min(replenishData.shop_sold, replenishData.available_quantity)}
                  required
                  disabled={replenishingRecord.product_type === 'perfume'}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {replenishingRecord.product_type === 'perfume'
                    ? 'Fixed at 1 Unit - will be bottled from bulk perfume'
                    : `Maximum: ${Math.min(replenishData.shop_sold, replenishData.available_quantity)} Unit`}
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowReplenishModal(false)}
                  className="px-6 py-3 text-gray-700 bg-gray-100 font-medium rounded-xl hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    replenishMutation.isPending ||
                    replenishQuantity <= 0 ||
                    (replenishingRecord.product_type !== 'perfume' && replenishQuantity > replenishData.available_quantity)
                  }
                  className="px-6 py-3 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {replenishMutation.isPending ? 'Replenishing...' : 'Replenish Stock'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutOfStockSales;
